import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Form, Button, Input, Checkbox, Typography } from 'antd';
import { ArrowLeftOutlined, LockOutlined, MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import IntlMessages from '../../components/IntlMessage';
import GoogleTranslateButton from '../../components/Language/GoogleTranslateButton';
import {
  login,
  verifyTotpLogin,
  setupTotpLogin,
  enableTotpLogin,
} from '../../redux/actions/AuthActions';

const { Text, Paragraph } = Typography;

const getQrImageUrl = (otpauthUrl) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUrl)}`;

class Login extends Component {
  state = {
    loading: false,
    step: 'credentials',
    pendingToken: null,
    loginEmail: '',
    totpLoading: false,
    setupLoading: false,
    setupData: null,
  };

  onFinish = (values) => {
    this.setState({ loading: true });

    this.props
      .login({
        email: values.email,
        password: values.password,
      })
      .then((result) => {
        if (result?.requiresTotpSetup) {
          this.setState(
            {
              step: 'totp-setup',
              pendingToken: result.pendingToken,
              loginEmail: values.email,
              loading: false,
              setupData: null,
            },
            () => this.loadSetupQr(),
          );
          return;
        }
        if (result?.requiresTotp) {
          this.setState({
            step: 'totp',
            pendingToken: result.pendingToken,
            loginEmail: values.email,
            loading: false,
          });
          return;
        }
        this.setState({ loading: false });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  loadSetupQr = () => {
    this.setState({ setupLoading: true });
    this.props
      .setupTotpLogin({ pending_token: this.state.pendingToken })
      .then((data) => {
        this.setState({ setupData: data, setupLoading: false });
      })
      .catch(() => {
        this.setState({ setupLoading: false });
      });
  };

  onVerifyTotp = (values) => {
    this.setState({ totpLoading: true });
    this.props
      .verifyTotpLogin({
        pending_token: this.state.pendingToken,
        code: String(values.code || '').replace(/\s/g, ''),
      })
      .then(() => {
        this.setState({ totpLoading: false });
      })
      .catch(() => {
        this.setState({ totpLoading: false });
      });
  };

  onEnableTotp = (values) => {
    this.setState({ totpLoading: true });
    this.props
      .enableTotpLogin({
        pending_token: this.state.pendingToken,
        code: String(values.code || '').replace(/\s/g, ''),
      })
      .then(() => {
        this.setState({ totpLoading: false });
      })
      .catch(() => {
        this.setState({ totpLoading: false });
      });
  };

  onBackToCredentials = () => {
    this.setState({
      step: 'credentials',
      pendingToken: null,
      totpLoading: false,
      setupLoading: false,
      setupData: null,
    });
  };

  renderCredentials() {
    const { loading } = this.state;
    return (
      <>
        <h1 className="sb-login-title">Welcome back</h1>
        <p className="sb-login-sub">Sign in to StrongBody Admin</p>

        <Form layout="vertical" onFinish={this.onFinish} size="large" requiredMark={false}>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" autoComplete="username" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              autoComplete="current-password"
            />
          </Form.Item>

          <div className="sb-login-meta">
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox style={{ color: '#6b7280' }}>Remember me</Checkbox>
            </Form.Item>
            <a href="/forgot-password" className="sb-login-link">
              Forgot password?
            </a>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button className="sb-login-btn" type="primary" htmlType="submit" block loading={loading}>
              <IntlMessages id="global.signin" />
            </Button>
          </Form.Item>
        </Form>
      </>
    );
  }

  renderTotpVerify() {
    const { totpLoading, loginEmail } = this.state;
    return (
      <>
        <div className="sb-totp-badge">
          <SafetyCertificateOutlined />
          Two-factor authentication
        </div>
        <h1 className="sb-login-title">Enter your code</h1>
        <p className="sb-login-sub">
          Open Google Authenticator (or any TOTP app) and enter the 6-digit code.
        </p>
        {loginEmail ? <p className="sb-totp-email">{loginEmail}</p> : null}

        <Form layout="vertical" onFinish={this.onVerifyTotp} size="large" requiredMark={false}>
          <Form.Item
            name="code"
            rules={[
              { required: true, message: 'Enter your authentication code' },
              {
                validator: (_, value) => {
                  const digits = String(value || '').replace(/\D/g, '');
                  if (digits.length !== 6) {
                    return Promise.reject(new Error('Code must be 6 digits'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            getValueFromEvent={(e) => String(e.target.value || '').replace(/\D/g, '').slice(0, 6)}
          >
            <Input
              className="sb-login-otp"
              placeholder="••••••"
              maxLength={6}
              inputMode="numeric"
              autoFocus
              autoComplete="one-time-code"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 12 }}>
            <Button
              className="sb-login-btn"
              type="primary"
              htmlType="submit"
              block
              loading={totpLoading}
            >
              Verify & continue
            </Button>
          </Form.Item>

          <Button
            type="link"
            block
            icon={<ArrowLeftOutlined />}
            onClick={this.onBackToCredentials}
            style={{ color: '#6b7280', height: 40 }}
          >
            Back to sign in
          </Button>
        </Form>
      </>
    );
  }

  renderTotpSetup() {
    const { totpLoading, setupLoading, setupData, loginEmail } = this.state;
    return (
      <>
        <div className="sb-totp-badge">
          <SafetyCertificateOutlined />
          2FA required
        </div>
        <h1 className="sb-login-title">Set up authenticator</h1>
        <p className="sb-login-sub">
          Super đã bắt buộc 2FA cho tài khoản này. Quét QR rồi nhập mã 6 số để tiếp tục.
        </p>
        {loginEmail ? <p className="sb-totp-email">{loginEmail}</p> : null}

        {setupLoading && <Paragraph type="secondary">Đang tạo mã QR…</Paragraph>}

        {setupData && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img
              src={getQrImageUrl(setupData.qr_code_url)}
              alt="2FA QR Code"
              style={{ width: 180, height: 180, marginBottom: 12 }}
            />
            <Paragraph copyable={{ text: setupData.secret }} style={{ marginBottom: 0 }}>
              Secret: <Text code>{setupData.secret}</Text>
            </Paragraph>
          </div>
        )}

        <Form layout="vertical" onFinish={this.onEnableTotp} size="large" requiredMark={false}>
          <Form.Item
            name="code"
            rules={[
              { required: true, message: 'Enter your authentication code' },
              {
                validator: (_, value) => {
                  const digits = String(value || '').replace(/\D/g, '');
                  if (digits.length !== 6) {
                    return Promise.reject(new Error('Code must be 6 digits'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            getValueFromEvent={(e) => String(e.target.value || '').replace(/\D/g, '').slice(0, 6)}
          >
            <Input
              className="sb-login-otp"
              placeholder="••••••"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 12 }}>
            <Button
              className="sb-login-btn"
              type="primary"
              htmlType="submit"
              block
              loading={totpLoading}
              disabled={!setupData}
            >
              Enable 2FA & continue
            </Button>
          </Form.Item>

          <Button
            type="link"
            block
            onClick={this.loadSetupQr}
            loading={setupLoading}
            style={{ color: '#6b7280', height: 36 }}
          >
            Tạo lại QR
          </Button>

          <Button
            type="link"
            block
            icon={<ArrowLeftOutlined />}
            onClick={this.onBackToCredentials}
            style={{ color: '#6b7280', height: 40 }}
          >
            Back to sign in
          </Button>
        </Form>
      </>
    );
  }

  render() {
    const { step } = this.state;
    const { authUser } = this.props;

    if (authUser) return <Redirect to="/" />;

    return (
      <div className="sb-login-root">
        <style>{`
          .sb-login-root {
            min-height: 100vh;
            font-family: "DM Sans", "Segoe UI", system-ui, sans-serif;
            background:
              radial-gradient(ellipse 80% 50% at 10% 0%, rgba(218, 31, 39, 0.12), transparent 55%),
              radial-gradient(ellipse 60% 40% at 90% 100%, rgba(17, 24, 39, 0.06), transparent 50%),
              linear-gradient(165deg, #faf8f6 0%, #f3eee8 45%, #ebe4dc 100%);
            display: flex;
            flex-direction: column;
          }
          .sb-login-topbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 28px;
          }
          .sb-login-shell {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 12px 20px 48px;
          }
          .sb-login-card {
            width: 100%;
            max-width: 420px;
            background: rgba(255,255,255,0.92);
            border: 1px solid rgba(17,24,39,0.06);
            border-radius: 20px;
            padding: 36px 32px 28px;
            box-shadow: 0 20px 50px rgba(17, 24, 39, 0.08);
            backdrop-filter: blur(8px);
          }
          .sb-login-brand {
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #da1f27;
            margin-bottom: 18px;
          }
          .sb-login-title {
            font-size: 28px;
            line-height: 1.2;
            font-weight: 700;
            color: #111827;
            margin: 0 0 8px;
          }
          .sb-login-sub {
            color: #6b7280;
            margin: 0 0 24px;
            font-size: 15px;
          }
          .sb-login-meta {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .sb-login-link {
            color: #da1f27;
            font-size: 13px;
          }
          .sb-login-btn {
            height: 48px;
            border-radius: 12px;
            background: #da1f27;
            border-color: #da1f27;
            font-weight: 600;
          }
          .sb-login-btn:hover, .sb-login-btn:focus {
            background: #b91820 !important;
            border-color: #b91820 !important;
          }
          .sb-totp-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 600;
            color: #da1f27;
            background: rgba(218, 31, 39, 0.08);
            border-radius: 999px;
            padding: 6px 12px;
            margin-bottom: 14px;
          }
          .sb-totp-email {
            font-size: 13px;
            color: #6b7280;
            margin: -12px 0 18px;
          }
          .sb-login-otp {
            letter-spacing: 0.35em;
            text-align: center;
            font-size: 22px;
            font-weight: 600;
          }
        `}</style>

        <div className="sb-login-topbar">
          <div className="sb-login-brand">StrongBody Admin</div>
          <GoogleTranslateButton />
        </div>

        <div className="sb-login-shell">
          <div className="sb-login-card">
            {step === 'credentials' && this.renderCredentials()}
            {step === 'totp' && this.renderTotpVerify()}
            {step === 'totp-setup' && this.renderTotpSetup()}
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    authUser: state.auth.authUser,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    login: (data) => dispatch(login(data)),
    verifyTotpLogin: (data) => dispatch(verifyTotpLogin(data)),
    setupTotpLogin: (data) => dispatch(setupTotpLogin(data)),
    enableTotpLogin: (data) => dispatch(enableTotpLogin(data)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Login);
